import { useTranslation } from 'react-i18next';
import { BasicResponsiveImage, Column, GridContainer, Link, Row, useScreenClass } from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import { sanitiseLocale } from '../../i18n/config';
import { useEnvContext } from '../../utils/environmentContext';
import { toSentenceCase } from '../../utils/helper';
import ErrorMessage from './../../components/ErrorMessage/ErrorMessage';
import { useTrackNavigationClicked } from './../../utils/analytics';
import styles, { stylesheet } from './ImageGallery.module.scss';
import { getColorName, getSimpleLayoutName } from './logo-style';
import SkeletonBox from './SkeletonBox';
import type { LogomakerLogo } from './gallery-logo-examples';

type SpanSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export interface columnSpanConfig {
    span: SpanSize;
    spanMd: SpanSize;
    spanSm: SpanSize;
    spanXs: SpanSize;
}
interface ImageGalleryProps {
    logoExamples: LogomakerLogo[];
    showErrorMessage: boolean;
    businessName: string;
    industry?: string;
    keywords: string[];
    keywordLongName?: string;
    isModalLayout: boolean;
}

const ImageGallery = ({
    businessName,
    logoExamples,
    showErrorMessage,
    industry = '',
    keywordLongName,
    keywords,
    isModalLayout,
}: ImageGalleryProps) => {
    useStyles(stylesheet);
    const screenClass = useScreenClass();
    const { i18n, t } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const { ENV_LOGOMAKER_BASE_URL, ENV_TRACKING_ENV } = useEnvContext();

    let columnSpanConfig: columnSpanConfig = {
        span: 2,
        spanMd: 3,
        spanSm: 4,
        spanXs: 6,
    };

    if (isModalLayout) {
        columnSpanConfig = {
            span: 6,
            spanMd: 6,
            spanSm: 6,
            spanXs: 6,
        };
    }

    const logomakerStudioRef = useTrackNavigationClicked(
        'LogomakerAnywhere',
        'LogomakerAnywhere',
        '/logomaker/studio/graphics',
        'editLogo',
    );

    if (showErrorMessage) {
        return <ErrorMessage />;
    }

    const generateLocalisedAltText = ({ signature }: LogomakerLogo): string => {
        const defaultAltText = t('Vista Logos > Logomaker Anywhere > Image Tile > Alt text.value');

        // Properties derived from the logo signature are EN only
        if (!locale?.includes('en')) {
            return defaultAltText;
        }

        const backgroundColor = getColorName(signature?.backgroundColor?.default ?? '');
        const fontStyle = signature?.companyName?.font?.default?.style?.replace(/_/g, ' ') ?? '';
        const layoutStyle = getSimpleLayoutName(signature?.layout?.default ?? '');

        if (!backgroundColor || !fontStyle || !layoutStyle) {
            return defaultAltText;
        }

        const article = (str: string): string => {
            if (!str) return '';
            return /^[aeiou]/i.test(str) ? 'an' : 'a';
        };

        const altText = toSentenceCase(
            `${keywordLongName ? `${keywordLongName} ` : ''}logo with ${article(backgroundColor)} ${backgroundColor} coloured background and ${article(fontStyle)} ${fontStyle} font in ${article(layoutStyle)} ${layoutStyle} layout with the name '${businessName}'.`,
        );

        return locale === 'en-US' ? altText.replace('coloured', 'colored') : altText;
    };

    return (
        <GridContainer>
            <Row as="ul">
                {logoExamples.map((galleryLogoExample, index) => (
                    <Column
                        as="li"
                        key={index}
                        {...columnSpanConfig}
                        marginBottom={{ xs: 5, sm: 5, md: 6, lg: 6, xl: 6 }}
                    >
                        <Link
                            href={getStudioUrl({
                                logoComposerId: galleryLogoExample.signature.logoComposerId,
                                businessName,
                                industry,
                                keywords,
                                screenClass,
                                localisedUrl: t('Logomaker landing page > canonicalUrl.value'),
                                logomakerBaseUrl: ENV_LOGOMAKER_BASE_URL,
                                env: ENV_TRACKING_ENV,
                            })}
                            ref={logomakerStudioRef}
                            style={{ display: 'block' }}
                        >
                            <BasicResponsiveImage
                                className={styles.logomakerTile}
                                aspectRatio={1}
                                src={galleryLogoExample.logo}
                                alt={generateLocalisedAltText(galleryLogoExample)}
                            />
                        </Link>
                    </Column>
                ))}
                {Array.from({ length: 12 - logoExamples.length }, (_, index) => (
                    <Column
                        as="li"
                        key={index}
                        {...columnSpanConfig}
                        marginBottom={{ xs: 5, sm: 5, md: 6, lg: 6, xl: 6 }}
                    >
                        <SkeletonBox />
                    </Column>
                ))}
            </Row>
        </GridContainer>
    );
};

const getStudioUrl = ({
    logoComposerId,
    businessName,
    industry,
    keywords,
    screenClass,
    localisedUrl,
    logomakerBaseUrl,
    env,
}: {
    logoComposerId: string;
    businessName: string;
    industry: string;
    keywords: string[];
    screenClass: string;
    localisedUrl: string;
    logomakerBaseUrl: string;
    env: string;
}) => {
    const keywordParams = [industry, ...keywords].filter(Boolean);
    const params = new URLSearchParams({
        logoId: logoComposerId,
        companyName: businessName,
        industryName: industry || 'Startups',
        keywords: keywordParams.toString(),
        tenant: 'logomaker-anywhere',
    });

    const getPatchedLocalisedUrl = (localisedUrl: string): string => {
        // TODO: Temporary patch to prevent 404 until fr-FR Logomaker Studio route is updated
        // localisedUrl is derived from the landing page canonical URL in Contentful.
        return localisedUrl.includes('.fr/creation-logo')
            ? 'https://www.vistaprint.fr/logo/creation-logo'
            : localisedUrl;
    };

    const baseUrl =
        env === 'prod'
            ? getPatchedLocalisedUrl(localisedUrl)
            : // TODO: Fix localised path for development
              logomakerBaseUrl;

    // Link users to /logomaker/studio/graphics as /logomaker/studio redirects
    // users to first route with query params removed
    if (screenClass === 'xs' || screenClass === 'sm') {
        return `${baseUrl}/studio?${params.toString()}`;
    } else {
        return `${baseUrl}/studio/graphics?${params.toString()}`;
    }
};

export default ImageGallery;
